import type { ComponentPropsWithoutRef, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

import type { ResponseProjectBlock } from '../../../../../../../shared/entities/ResponseProjectBlock';
import { useGetAllResponseProjectBlocks } from '../../../hooks/useGetAllResponseProjectBlocks.hooks';
import { ResponseProjectCreateBlock } from '../components/responseProjectCreateBlock/ResponseProjectCreateBlock';
import { ResponseProjectDeleteBlock } from '../components/responseProjectDeleteBlock/ResponseProjectDeleteBlock';
import { ResponseProjectUpdateBlock } from '../components/responseProjectUpdateBlock/ResponseProjectUpdateBlock';

interface ResponseProjectDetailContextType {
  blocks: ResponseProjectBlock[];
  openedBlockIds: number[];
  openBlocks: (ids: number[]) => void;
  closeBlocks: (ids: number[]) => void;
  getBlockHTMLId: (blockId: number) => string;
  scrollToBlock: (blockId: number) => void;
  scrollAreaRef: React.RefObject<HTMLDivElement>;
  onOpenCreateBlockModal: (data: CreateBlockModalState) => void;
  onCloseCreateBlockModal: () => void;
  onOpenUpdateBlockModal: (block: UpdateBlockModalState) => void;
  onCloseUpdateBlockModal: () => void;
  onOpenDeleteBlockModal: (block: DeleteBlockModalState) => void;
  onCloseDeleteBlockModal: () => void;
  projectId: number;
  onUpdateBlockContent: (blockId: number) => void;
  isBlockContentUpdated: (blockId: number) => boolean;
}

export const PROJECT_DETAIL_SCROLL_AREA_VIEWPORT_ID =
  'project-detail-scroll-area-viewport';
interface ResponseProjectDetailProviderProps extends PropsWithChildren {
  projectId: number;
}

const ResponseProjectDetailContext = createContext<
  ResponseProjectDetailContextType | undefined
>(undefined);

export const ResponseProjectDetailProvider = ({
  children,
  projectId,
}: ResponseProjectDetailProviderProps) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [openedBlockIds, setOpenedBlockIds] = useState<number[]>([]);
  const { data } = useGetAllResponseProjectBlocks(projectId);
  const {
    Modal: CreateBlockModal,
    onOpenCreateBlockModal,
    onCloseCreateBlockModal,
  } = useCreateBlockModal();

  const {
    Modal: UpdateBlockModal,
    onOpenUpdateBlockModal,
    onCloseUpdateBlockModal,
  } = useUpdateBlockModal();

  const {
    Modal: DeleteBlockModal,
    onOpenDeleteBlockModal,
    onCloseDeleteBlockModal,
  } = useDeleteBlockModal();

  const [updatedBlockContentId, setUpdatedBlockContentId] = useState<number[]>(
    [],
  );

  const openBlocks = (ids: number[]) => {
    setOpenedBlockIds(prev => [
      ...prev.filter(id => !ids.includes(id)),
      ...ids,
    ]);
  };

  const closeBlocks = (ids: number[]) => {
    console.log('closeBlocks', ids, openedBlockIds);
    setOpenedBlockIds(prev => prev.filter(id => !ids.includes(id)));
  };
  console.log('openedBlockIds', openedBlockIds);

  const onUpdateBlockContent = (blockId: number) => {
    setUpdatedBlockContentId(prev => [...prev, blockId]);
    setTimeout(() => {
      setUpdatedBlockContentId(prev => prev.filter(id => id !== blockId));
    }, 2000);
  };

  const isBlockContentUpdated = (blockId: number) => {
    return updatedBlockContentId.includes(blockId);
  };

  useEffect(() => {
    if (data && openedBlockIds.length === 0) {
      const blockToOpen = data.blocks.flatMap(block =>
        block.getIdOfChildrenAndSelf({
          includeOnlyBlockWithChildren: true,
        }),
      );
      if (blockToOpen?.length) {
        openBlocks(blockToOpen);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <ResponseProjectDetailContext.Provider
      value={{
        getBlockHTMLId,
        projectId,
        blocks: data?.blocks ?? [],
        openedBlockIds,
        openBlocks,
        closeBlocks,
        scrollToBlock,
        scrollAreaRef,
        onOpenCreateBlockModal,
        onCloseCreateBlockModal,
        onUpdateBlockContent,
        isBlockContentUpdated,
        onOpenUpdateBlockModal,
        onCloseUpdateBlockModal,
        onOpenDeleteBlockModal,
        onCloseDeleteBlockModal,
      }}
    >
      {children}
      <CreateBlockModal />
      <UpdateBlockModal />
      <DeleteBlockModal />
    </ResponseProjectDetailContext.Provider>
  );
};

export const useResponseProjectDetailContext = () => {
  const context = useContext(ResponseProjectDetailContext);
  if (!context) {
    throw new Error(
      'useResponseProjectDetailContext must be used within a ResponseProjectDetailProvider',
    );
  }
  return context;
};

function scrollToBlock(blockId: number) {
  const scrollAreaViewport = document.querySelector(
    `#${PROJECT_DETAIL_SCROLL_AREA_VIEWPORT_ID} .mantine-ScrollArea-viewport`,
  );

  if (scrollAreaViewport) {
    const blockScrollPosition = getBlockScrollPositionByBlockId(blockId) - 50;

    scrollAreaViewport.scrollTo({
      top: blockScrollPosition,
      behavior: 'smooth',
    });
  }
}

function getBlockScrollPositionByBlockId(blockId: number) {
  const element = document.getElementById(getBlockHTMLId(blockId));
  if (!element) {
    return 0;
  }
  return element.offsetTop;
}

function getBlockHTMLId(blockId: number) {
  return `block-${blockId}`;
}

type CreateBlockModalState = ComponentPropsWithoutRef<
  typeof ResponseProjectCreateBlock
>['data'];

/**
 * @name useCreateBlockModal
 * @description Manage and render the create modal
 */
const useCreateBlockModal = () => {
  const [createBlockModalState, setCreateBlockModalState] = useState<
    CreateBlockModalState | undefined
  >(undefined);

  const onOpen = (data: CreateBlockModalState) => {
    setCreateBlockModalState(data);
  };

  const onClose = () => {
    setCreateBlockModalState(undefined);
  };

  const Modal = () => {
    return (
      <ResponseProjectCreateBlock
        isOpen={!!createBlockModalState}
        data={createBlockModalState}
        onClose={onClose}
      />
    );
  };

  return {
    Modal,
    onOpenCreateBlockModal: onOpen,
    onCloseCreateBlockModal: onClose,
  };
};

type UpdateBlockModalState = ComponentPropsWithoutRef<
  typeof ResponseProjectUpdateBlock
>['block'];

/**
 * @name useUpdateBlockModal
 * @description Manage and render the update modal with the block to update
 */
const useUpdateBlockModal = () => {
  const [updateBlockModalState, setUpdateBlockModalState] = useState<
    UpdateBlockModalState | undefined
  >(undefined);

  const onOpen = (data: UpdateBlockModalState) => {
    setUpdateBlockModalState(data);
  };

  const onClose = () => {
    setUpdateBlockModalState(undefined);
  };

  const Modal = () => {
    if (!updateBlockModalState) {
      return null;
    }

    return (
      <ResponseProjectUpdateBlock
        isOpen={!!updateBlockModalState}
        block={updateBlockModalState}
        onClose={onClose}
      />
    );
  };

  return {
    Modal,
    onOpenUpdateBlockModal: onOpen,
    onCloseUpdateBlockModal: onClose,
  };
};

type DeleteBlockModalState = ComponentPropsWithoutRef<
  typeof ResponseProjectDeleteBlock
>['block'];

/**
 * @name useUpdateBlockModal
 * @description Manage and render the update modal with the block to update
 */
const useDeleteBlockModal = () => {
  const [deleteBlockModalState, setDeleteBlockModalState] = useState<
    DeleteBlockModalState | undefined
  >(undefined);

  const onOpen = (data: DeleteBlockModalState) => {
    setDeleteBlockModalState(data);
  };

  const onClose = () => {
    setDeleteBlockModalState(undefined);
  };

  const Modal = () => {
    if (!deleteBlockModalState) {
      return null;
    }

    return (
      <ResponseProjectDeleteBlock
        isOpen={!!deleteBlockModalState}
        block={deleteBlockModalState}
        onClose={onClose}
      />
    );
  };

  return {
    Modal,
    onOpenDeleteBlockModal: onOpen,
    onCloseDeleteBlockModal: onClose,
  };
};
